// Story 8.15 (ADO-2024), D-9 and D-10: what happened to the caller's auto-sent carts.
//
//   GET  /api/v1/auto-send-outcomes                    the undismissed outcomes, newest first
//   POST /api/v1/auto-send-outcomes/{id}/dismissal     dismiss one, for everyone on the cart
//
// An outcome is listed when an auto-send went while HHT orders were waiting
// (`SENT_WITH_HHT_WAITING`, C-1), or did not go (`REFUSED`, `MISSED`), and only for the carts the
// caller may order for. The `reason` is the server's plain-words copy. The wire shape is a
// LOCK-STEP COPY of order-capture's `AutoSendOutcomeDto`; keep the two in sync. Every body is
// validated; an outcome of a kind this client does not know is left out (a newer server), and
// anything else in the wrong shape is a contract violation, surfaced as an error.

import type { OrderingEntryKind } from './ordering-choices.client';
import { API_PREFIX, apiFetch } from './shoplink-api';

export const AUTO_SEND_OUTCOMES_PATH = `${API_PREFIX}/auto-send-outcomes`;

export const AUTO_SEND_OUTCOME_KINDS = ['SENT_WITH_HHT_WAITING', 'REFUSED', 'MISSED'] as const;
export type AutoSendOutcomeKind = (typeof AUTO_SEND_OUTCOME_KINDS)[number];

export interface AutoSendOutcome {
  readonly id: string;
  /** The depot's id, as the web addresses depots. */
  readonly depotId: string;
  readonly depotName: string;
  readonly customerAccountCode: string;
  /** The kind of the ordering entry the cart is reached through, so two carts at one depot (Chill
   *  and Fruit & Veg at 25) are told apart; absent from a server that does not send it. */
  readonly entryKind?: OrderingEntryKind;
  readonly kind: AutoSendOutcomeKind;
  /** Why it did not go, in plain words (`REFUSED`, `MISSED`); `null` otherwise. */
  readonly reason: string | null;
  /** The order it placed (`SENT_WITH_HHT_WAITING`); `null` otherwise. */
  readonly orderNumber: string | null;
  /** When the auto-send ran (ISO 8601 UTC). */
  readonly ranAt: string;
}

const KINDS: ReadonlySet<unknown> = new Set(AUTO_SEND_OUTCOME_KINDS);

const SHAPE_ERROR = 'Auto-send outcomes response was not in the expected shape';

/** The outcome in `value`, `null` for a well-formed one of an unknown kind; throws otherwise. */
function outcomeOf(value: unknown): AutoSendOutcome | null {
  const record = (typeof value === 'object' && value !== null ? value : {}) as Record<
    string,
    unknown
  >;
  const {
    id,
    depotId,
    depotName,
    customerAccountCode,
    entryKind,
    kind,
    reason,
    orderNumber,
    ranAt,
  } = record;
  if (
    typeof id !== 'string' ||
    typeof depotId !== 'string' ||
    typeof depotName !== 'string' ||
    typeof customerAccountCode !== 'string' ||
    (entryKind !== undefined && typeof entryKind !== 'string') ||
    typeof kind !== 'string' ||
    (reason !== null && typeof reason !== 'string') ||
    (orderNumber !== null && typeof orderNumber !== 'string') ||
    typeof ranAt !== 'string' ||
    Number.isNaN(Date.parse(ranAt))
  ) {
    throw new Error(SHAPE_ERROR);
  }
  if (!KINDS.has(kind)) return null;
  return {
    id,
    depotId,
    depotName,
    customerAccountCode,
    ...(entryKind === undefined ? {} : { entryKind: entryKind as OrderingEntryKind }),
    kind: kind as AutoSendOutcomeKind,
    reason,
    orderNumber,
    ranAt,
  };
}

/** The caller's undismissed outcomes. Any non-2xx is an error. */
export async function listAutoSendOutcomes(
  options: { readonly signal?: AbortSignal } = {},
): Promise<readonly AutoSendOutcome[]> {
  const res = await apiFetch(
    AUTO_SEND_OUTCOMES_PATH,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to load the auto-send outcomes (${res.status})`);
  const body: unknown = await res.json();
  if (!Array.isArray(body)) throw new Error(SHAPE_ERROR);
  return body.flatMap((entry) => {
    const outcome = outcomeOf(entry);
    return outcome === null ? [] : [outcome];
  });
}

/** Dismiss the outcome `id` (its banner stops showing for everyone on the cart). */
export async function dismissAutoSendOutcome(id: string): Promise<void> {
  const res = await apiFetch(`${AUTO_SEND_OUTCOMES_PATH}/${encodeURIComponent(id)}/dismissal`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to dismiss the auto-send outcome (${res.status})`);
}
